// src/state/useServiceStatus.ts
import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";

/** Keep this in sync with your routes / drawer entries */
export type ServiceId =
  | "goalbuddy"
  | "omnicast"
  | "omnicine"
  | "synkstrive"
  | "coaches"
  | "smart-skins"
  | "smart-tattoos"
  | "nft";

export type ServiceStatus = {
  live: boolean;               // true while recording/streaming/active
  label?: string;              // e.g., "Broadcasting", "Recording", "Idle"
  ts?: number;                 // last change timestamp (ms)
  meta?: Record<string, any>;  // extras (sessionId, bitrate, etc.)
};

type State = {
  statuses: Record<ServiceId, ServiceStatus>;

  /** Replace an entire status for a service */
  set: (id: ServiceId, status: ServiceStatus) => void;
  /** Patch fields of a status for a service */
  patch: (id: ServiceId, patch: Partial<ServiceStatus>) => void;

  /** Convenience helpers used across the app */
  setLive: (id: ServiceId, live: boolean, label?: string) => void;
  startLive: (id: ServiceId, label?: string, meta?: Record<string, any>) => void;
  stopLive: (id: ServiceId, label?: string) => void;
  heartbeat: (id: ServiceId, label?: string) => void; // update ts/label without toggling live

  /** Reset everything to initial idle */
  reset: () => void;
};

const INIT_STATUS: ServiceStatus = { live: false, label: "Idle", ts: undefined };

export const SERVICES: ServiceId[] = [
  "goalbuddy",
  "omnicast",
  "omnicine",
  "synkstrive",
  "coaches",
  "smart-skins",
  "smart-tattoos",
  "nft",
];

function makeInitialMap(): Record<ServiceId, ServiceStatus> {
  return Object.fromEntries(SERVICES.map((k) => [k, { ...INIT_STATUS }])) as Record<ServiceId, ServiceStatus>;
}

/** SSR-safe storage fallback (memory) so Vite build won't choke */
const memoryStorage: Storage = {
  getItem: (k) => (memoryStorage as any)._m?.get(k) ?? null,
  setItem: (k, v) => { (memoryStorage as any)._m ??= new Map(); (memoryStorage as any)._m.set(k, String(v)); },
  removeItem: (k) => { (memoryStorage as any)._m?.delete(k); },
  clear: () => { (memoryStorage as any)._m = new Map(); },
  key: (i) => {
    const key = Array.from(((memoryStorage as any)._m ?? new Map()).keys())[i];
    return key == null ? null : String(key);
  },
  get length() { return ((memoryStorage as any)._m ?? new Map()).size; }
};

const storage = createJSONStorage<Partial<State>>(() =>
  typeof window !== "undefined" && typeof window.sessionStorage !== "undefined"
    ? window.sessionStorage
    : memoryStorage
);

export const useServiceStatus = create<State>()(
  persist(
    (set, get) => ({
      statuses: makeInitialMap(),

      set: (id, status) =>
        set((s) => ({ statuses: { ...s.statuses, [id]: { ...status } } })),

      patch: (id, patch) =>
        set((s) => ({
          statuses: {
            ...s.statuses,
            [id]: { ...s.statuses[id], ...patch },
          },
        })),

      setLive: (id, live, label) =>
        set((s) => ({
          statuses: {
            ...s.statuses,
            [id]: {
              ...(s.statuses[id] ?? INIT_STATUS),
              live,
              label: label ?? (live ? "Live" : "Idle"),
              ts: Date.now(),
            },
          },
        })),

      startLive: (id, label = "Live", meta) =>
        set((s) => ({
          statuses: {
            ...s.statuses,
            [id]: {
              live: true,
              label,
              ts: Date.now(),
              meta: { ...(s.statuses[id]?.meta ?? {}), ...(meta ?? {}) },
            },
          },
        })),

      stopLive: (id, label = "Idle") =>
        set((s) => ({
          statuses: {
            ...s.statuses,
            [id]: {
              live: false,
              label,
              ts: Date.now(),
              meta: s.statuses[id]?.meta,
            },
          },
        })),

      heartbeat: (id, label) =>
        set((s) => ({
          statuses: {
            ...s.statuses,
            [id]: {
              ...(s.statuses[id] ?? INIT_STATUS),
              label: label ?? s.statuses[id]?.label,
              ts: Date.now(),
            },
          },
        })),

      reset: () => ({ statuses: makeInitialMap() }),
    }),
    {
      name: "service-status",
      storage,                                // session-scoped (with SSR-safe fallback)
      partialize: (s) => ({ statuses: s.statuses }),
      version: 1,
    }
  )
);

/* --------------------------- Selectors --------------------------- */

/** true if the service is currently live */
export const useServiceLive = (id: ServiceId) =>
  useServiceStatus((s) => !!s.statuses[id]?.live);

/** label for the service, defaults to "Idle" */
export const useServiceLiveLabel = (id: ServiceId) =>
  useServiceStatus((s) => s.statuses[id]?.label ?? "Idle");

/** last change timestamp (ms since epoch); undefined if never set */
export const useServiceTimestamp = (id: ServiceId) =>
  useServiceStatus((s) => s.statuses[id]?.ts);

/** full status object (handy for advanced UIs) */
export const useService = (id: ServiceId) =>
  useServiceStatus((s) => s.statuses[id] ?? INIT_STATUS);

/** true if any service is live (useful for global indicators) */
export const useAnyServiceLive = () =>
  useServiceStatus((s) => SERVICES.some((id) => !!s.statuses[id]?.live));

/* ---------------------- Imperative helpers ---------------------- */

export function startLive(id: ServiceId, label?: string, meta?: Record<string, any>) {
  useServiceStatus.getState().startLive(id, label, meta);
}

export function setLive(id: ServiceId, live: boolean, label?: string) {
  useServiceStatus.getState().setLive(id, live, label);
}

export function stopLive(id: ServiceId, label?: string) {
  useServiceStatus.getState().stopLive(id, label);
}

export function heartbeat(id: ServiceId, label?: string) {
  useServiceStatus.getState().heartbeat(id, label);
}

export function resetServiceStatus() {
  useServiceStatus.getState().reset();
}


